import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import type { SchoolImportResult as SchoolImportResultData } from '~/types/school'

type SchoolImportResultProps = {
  result: SchoolImportResultData
}

/**
 * Presentational summary of a completed CSV import: imported/updated/skipped counts
 * plus a per-row error table (row number is the 1-based CSV line number
 * returned by the server, `reason` rendered verbatim — it's a raw
 * server-generated English string, not run through i18n). Rendered inside
 * `SchoolImportDialog`, which widens and caps the height of the dialog body
 * (not this component) when `result.errors.length > 0`, so this table has no
 * scroll container of its own — nesting one here would create two
 * independent scrollbars.
 */
export function SchoolImportResult({ result }: SchoolImportResultProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4 border-t pt-4">
      <p className="font-semibold">{t('schools.resultTitle')}</p>
      <div className="flex gap-8">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{t('schools.imported')}</p>
          <p className="text-2xl font-semibold">{result.imported}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{t('schools.updated')}</p>
          <p className="text-2xl font-semibold">{result.updated}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{t('schools.skipped')}</p>
          <p className="text-2xl font-semibold">{result.skipped}</p>
        </div>
      </div>
      {result.errors.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">{t('schools.row')}</TableHead>
                <TableHead>{t('schools.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.errors.map((error) => (
                <TableRow key={`${error.row}-${error.reason}`}>
                  <TableCell>{error.row}</TableCell>
                  <TableCell>{error.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('schools.noErrors')}</p>
      )}
    </div>
  )
}
